/**
 * Persisted UI preferences for the teleconsultation sidebar: which collapsible
 * sections are open, and which clinics were recently picked (so a regional
 * admin's dozens of connected clinics float the useful handful to the top).
 *
 * Pure localStorage helpers — deliberately dumb, no reactivity.
 *
 * Session hygiene splits the two on what they reveal, not on where they live:
 *  - Section open/closed state is impersonal UI chrome (which accordion the
 *    last person left open), so it survives logout like any other preference.
 *  - The recent-clinic list is user-scoped BEHAVIOURAL data: it records which
 *    hospitals a reader was actually working in. On a shared workstation that
 *    would leak the previous radiologist's clinic history into the next
 *    reader's rail ordering, so clearRecentClinics() joins the logout/login
 *    hygiene sweep in use-auth (see clearPerTabSessionMemory).
 */
const SECTIONS_KEY = 'tc.sidebar.sections';
const RECENT_CLINICS_KEY = 'tc.sidebar.recentClinics';
/** Recency list cap — enough to float a reader's usual hospitals, no more. */
export const RECENT_CLINICS_MAX = 5;
/** Status + Clinics open by default; the long-tail sections start collapsed. */
export const DEFAULT_SECTION_STATE = {
    status: true,
    clinics: true,
    regions: false,
    groups: false,
    diagnosis: false,
};
/** Load persisted open/closed state, merged over defaults; corrupt or missing storage falls back to defaults. */
export function loadSectionState() {
    const next = { ...DEFAULT_SECTION_STATE };
    try {
        const raw = localStorage.getItem(SECTIONS_KEY);
        if (!raw)
            return next;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            for (const id of Object.keys(next)) {
                const value = parsed[id];
                if (typeof value === 'boolean')
                    next[id] = value;
            }
        }
        return next;
    }
    catch {
        return { ...DEFAULT_SECTION_STATE };
    }
}
export function saveSectionState(state) {
    try {
        localStorage.setItem(SECTIONS_KEY, JSON.stringify(state));
    }
    catch {
        // storage unavailable — preference just won't survive the reload
    }
}
/** Most-recently-picked clinic ids, newest first. */
export function loadRecentClinics() {
    try {
        const raw = localStorage.getItem(RECENT_CLINICS_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter((v) => typeof v === 'string').slice(0, RECENT_CLINICS_MAX);
    }
    catch {
        return [];
    }
}
/** Record a clinic pick: dedupes, prepends, caps, persists. Returns the new list. */
export function pushRecentClinic(clinicId) {
    const next = [clinicId, ...loadRecentClinics().filter((id) => id !== clinicId)].slice(0, RECENT_CLINICS_MAX);
    try {
        localStorage.setItem(RECENT_CLINICS_KEY, JSON.stringify(next));
    }
    catch {
        // storage unavailable
    }
    return next;
}
/**
 * Drop the recent-clinic history. Called from the auth hygiene sweep on logout
 * AND on login: the list is a record of which clinics a reader worked in, so it
 * must not follow one radiologist's session into the next one on a shared
 * workstation. Section open/closed state is deliberately left alone.
 */
export function clearRecentClinics() {
    try {
        localStorage.removeItem(RECENT_CLINICS_KEY);
    }
    catch {
        // storage unavailable — nothing was stored anyway
    }
}
